import React, { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { auth, db, storage } from "../../firebase";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import {
  getEstimatedResultOptions,
  getLeadFields,
  getStudentResultOptions,
} from "../../formSchemas/leadSchemas";
import { FieldConfig, FormProps } from "./FormComponent/types";
import { formatDateForInput } from "../../utils/date";
import GroupField from "./FormComponent/GroupField";
import countryCode from "../../assets/countryCode.json";

interface LeadFormProps extends FormProps {
  initialData?: Record<string, any>;
  disabled?: boolean;
}

const LeadForm: React.FC<LeadFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  disabled = false,
}) => {
  const [fields, setFields] = useState<FieldConfig[]>([]);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loadingFields, setLoadingFields] = useState(true);
  const [currentUserRole, setCurrentUserRole] = useState<string>("");
  const [currentUserUid, setCurrentUserUid] = useState<string>("");
  const [currentUserDisplayName, setCurrentUserDisplayName] =
    useState<string>("");
  const isEdit = !!initialData?.id;
  const [selectedFileName, setSelectedFileName] = useState<string>("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);

  // Fetch current user's role, UID, and displayName
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const fetchUserData = async () => {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setCurrentUserRole(data.role || "");
          setCurrentUserUid(user.uid);
          setCurrentUserDisplayName(data.displayName || "");
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
      }
    };

    fetchUserData();
  }, []);

  // Fetch fields and dynamic assignee options
  useEffect(() => {
    const fetchFields = async () => {
      try {
        let usersOptions: { uid: string; displayName: string }[] = [];
        let managerBranch = null;
        const isPrivilegedUser =
          currentUserRole === "admin" ||
          currentUserRole === "super admin" ||
          currentUserRole === "IT";
        const isBranchManager = currentUserRole === "Branch Manager";
        if (isPrivilegedUser) {
          const q = query(
            collection(db, "users"),
            where("role", "in", [
              "sales",
              "admin",
              "super admin",
              "part-time sales",
            ]),
          );
          const snapshot = await getDocs(q);
          usersOptions = snapshot.docs.map((docSnap) => ({
            displayName: docSnap.data().displayName,
            uid: docSnap.id,
          }));
        } else if (isBranchManager) {
          // Get branch for current user
          const userDoc = await getDoc(doc(db, "users", currentUserUid));
          managerBranch = userDoc.exists() ? userDoc.data().branch : null;
          if (managerBranch) {
            const q = query(
              collection(db, "users"),
              where("branch", "==", managerBranch),
              where("role", "in", [
                "sales",
                "part-time sales",
                "Branch Manager",
              ]),
            );
            const snapshot = await getDocs(q);
            usersOptions = snapshot.docs.map((docSnap) => ({
              displayName: docSnap.data().displayName,
              uid: docSnap.id,
            }));
          } else {
            usersOptions = [];
          }
        }
        // For restricted users, only self
        else if (currentUserUid && currentUserDisplayName) {
          usersOptions = [
            { displayName: currentUserDisplayName, uid: currentUserUid },
          ];
        }
        const leadFields = getLeadFields([]);
        const updatedFields = leadFields.map((f) => {
          if (f.name === "assignee") {
            return {
              ...f,
              optionsData: usersOptions as {
                uid: string;
                displayName: string;
              }[],
            };
          }
          if (f.name === "estimatedResult") {
            return {
              ...f,
              options: getEstimatedResultOptions(initialData?.qualification),
            };
          }
          if (f.name === "studentResult") {
            return {
              ...f,
              options: getStudentResultOptions(initialData?.qualification),
            };
          }
          return f;
        });
        setFields(updatedFields);
        // Set default assignee
        const today = new Date();
        const defaultAssignee =
          !isEdit &&
          currentUserUid &&
          currentUserDisplayName &&
          !initialData?.assigneeId
            ? {
                assignee: currentUserDisplayName,
                assigneeId: currentUserUid,
              }
            : {};
        setFormData({
          createdDate: formatDateForInput(today),
          ...defaultAssignee,
          ...(initialData || {}),
        });
      } catch (err) {
        console.error("Error fetching fields:", err);
      } finally {
        setLoadingFields(false);
      }
    };
    fetchFields();
  }, [initialData, currentUserRole, currentUserUid, currentUserDisplayName]);

  // Handle changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;

    // 🔹 MODIFIED: Special handling for assignee select
    if (name === "assignee") {
      const assigneeField = fields.find((f) => f.name === "assignee");
      const selectedUser = assigneeField?.optionsData?.find(
        (u: any) => u.uid === value,
      );
      setFormData({
        ...formData,
        assignee: selectedUser?.displayName || "",
        assigneeId: selectedUser?.uid || "",
      });
      setErrors((prev) => ({ ...prev, assignee: "" }));
      return;
    }

    if (name === "country") {
      const selectedCountry = countryCode.find((c) => c.countryName === value);
      setFormData({
        ...formData,
        country: value,
        countryCode: selectedCountry ? `+${selectedCountry.countryCode}` : "",
      });
      setErrors((prev) => ({ ...prev, country: "" }));
      return;
    }

    if (name === "qualification") {
      const estimatedOptions = getEstimatedResultOptions(value);
      const studentResultOptions = getStudentResultOptions(value);
      setFields((prev) =>
        prev.map((field) =>
          field.name === "estimatedResult"
            ? { ...field, options: estimatedOptions }
            : field.name === "studentResult"
              ? { ...field, options: studentResultOptions }
            : field,
        ),
      );
      setFormData({
        ...formData,
        qualification: value,
        studentResult: studentResultOptions.includes(formData.studentResult)
          ? formData.studentResult
          : "",
        estimatedResult: estimatedOptions.includes(formData.estimatedResult)
          ? formData.estimatedResult
          : "",
      });
      setErrors((prev) => ({
        ...prev,
        qualification: "",
        studentResult: "",
        estimatedResult: "",
      }));
      return;
    }

    // Default handling
    setFormData({ ...formData, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (file.type !== "application/pdf") {
        setErrors((prev) => ({
          ...prev,
          uploadedResult: "Only PDF files are allowed",
        }));
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          uploadedResult: "File size must be less than 5MB",
        }));
        return;
      }
      setSelectedFile(file);
      setSelectedFileName(file.name);
      setErrors((prev) => ({ ...prev, uploadedResult: "" }));
    } else {
      setSelectedFile(null);
      setSelectedFileName("");
    }
  };

  const validateFields = (): boolean => {
    const newErrors: Record<string, string> = {};
    const name = (formData.name || "").trim();
    if (!/^[A-Za-z\s]+$/.test(name)) {
      newErrors.name = "Name must only contain letters and spaces.";
    }
    const ic = (formData.ic || "").trim();
    const icPattern = /^\d{12}$/;
    if (ic && !icPattern.test(ic)) {
      newErrors.ic =
        "IC must contain exactly 12 digits with no symbols or letters.";
    }
    // const phone = (formData.phoneNumber || "").trim();
    // const phonePattern = /^(\+?60|0)1\d{8,9}$/;
    // if (!phonePattern.test(phone)) {
    //   newErrors.phoneNumber =
    //     "Invalid Malaysian phone number. Example: 0123456789 or +60123456789";
    // }
    const email = (formData.email || "").trim();
    if (email) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(email)) {
        newErrors.email = "Invalid email address.";
      }
    }
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = Object.keys(newErrors)[0];
      const errorElement = document.getElementById(`field-${firstErrorField}`);
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (Object.values(errors).some(Boolean)) return;
    if (!validateFields()) return;

    try {
      setIsUploading(true);
      const isSalesUser =
        currentUserRole === "sales" || currentUserRole === "part-time sales";

      const submitData = { ...formData };

      // For sales users creating new leads, ensure assignee is set to current user
      if (isSalesUser && !isEdit) {
        submitData.assignee = currentUserDisplayName || "";
        submitData.assigneeId = currentUserUid || "";
      }

      // Upload PDF file if selected
      if (selectedFile) {
        try {
          // Generate a unique file name
          const timestamp = Date.now();
          const fileName = `${timestamp}_${selectedFile.name}`;
          // Use user's UID for storage path to comply with Firebase Storage rules
          const storagePath = `leads/${fileName}`;
          const storageRef = ref(storage, storagePath);

          // Upload file with progress tracking
          const uploadTask = uploadBytesResumable(storageRef, selectedFile);

          await new Promise<void>((resolve, reject) => {
            uploadTask.on(
              "state_changed",
              (snapshot) => {
                const progress =
                  (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                setUploadProgress(progress);
              },
              (error) => {
                console.error("Upload error:", error);
                reject(error);
              },
              async () => {
                // Get download URL
                const downloadURL = await getDownloadURL(
                  uploadTask.snapshot.ref,
                );
                submitData.uploadedResult = downloadURL;
                resolve();
              },
            );
          });
        } catch (uploadError: any) {
          console.error("File upload failed:", uploadError);
          setErrors((prev) => ({
            ...prev,
            uploadedResult: "File upload failed. Please try again.",
          }));
          setIsUploading(false);
          return;
        }
      }

      if (onSubmit) await onSubmit(submitData);
      setIsUploading(false);
    } catch (err: any) {
      console.error("Form submit error:", err);
      alert(err.message || "Failed to submit lead form.");
      setIsUploading(false);
    }
  };

  if (loadingFields) {
    return <p className="p-4 text-gray-600">Loading lead form...</p>;
  }

  const isSalesUser =
    currentUserRole === "sales" || currentUserRole === "part-time sales";

  const isRestrictedUser =
    currentUserRole !== "admin" &&
    currentUserRole !== "super admin" &&
    currentUserRole !== "IT" &&
    currentUserRole !== "Branch Manager";

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 overflow-auto">
      <div
        className={`grid gap-4 ${
          fields.length > 8 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"
        }`}
      >
        {fields.map((field) => {
          const widthClass = fields.length > 8 ? "w-full" : "w-60";

          return (
            <div
              key={field.name}
              id={`field-${field.name}`}
              className="flex w-full flex-col"
            >
              <div className="flex w-full">
                <label
                  htmlFor={field.name}
                  className="w-60 pt-2 text-gray-800 font-medium"
                >
                  {field.label}:
                </label>
                {field.type === "group" ? (
                  <div className={`${widthClass}`}>
                    <GroupField
                      value={{
                        countryCode: formData.countryCode || "",
                        phoneNumber: formData.phoneNumber || "",
                      }}
                      onChange={(updatedObj) =>
                        setFormData({
                          ...formData,
                          countryCode: updatedObj.countryCode || "",
                          phoneNumber: updatedObj.phoneNumber || "",
                        })
                      }
                      groupFields={field.groupField || []}
                      widthClass={widthClass}
                      required={field.required}
                    />
                  </div>
                ) : field.name === "assignee" &&
                  currentUserRole !== "admin" &&
                  currentUserRole !== "super admin" &&
                  currentUserRole !== "IT" &&
                  currentUserRole !== "Branch Manager" ? (
                  <input
                    type="text"
                    name="assignee"
                    value={currentUserDisplayName || ""}
                    disabled
                    className={`${widthClass} p-1 border border-gray-600 rounded-md disabled:bg-gray-100`}
                  />
                ) : field.type === "select" ? (
                  // 🔹 MODIFIED: Use optionsData for assignee
                  <select
                    name={field.name}
                    value={
                      field.name === "assignee"
                        ? formData.assigneeId || ""
                        : formData[field.name] || ""
                    }
                    onChange={handleChange}
                    disabled={
                      disabled ||
                      field.disabled ||
                      (field.name === "assignee" && isRestrictedUser)
                    }
                    required={field.required}
                    className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                  >
                    <option value="">Select</option>
                    {(field.name === "assignee"
                      ? field.optionsData
                      : field.options
                    )?.map((opt: any) =>
                      field.name === "assignee" ? (
                        <option key={opt.uid} value={opt.uid}>
                          {opt.displayName}
                        </option>
                      ) : (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ),
                    )}
                  </select>
                ) : field.type === "textarea" ? (
                  <textarea
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    disabled={disabled || field.disabled}
                    rows={4}
                    className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                  />
                ) : field.type === "file" ? (
                  <div className="w-full">
                    {/* Custom File Input */}
                    <label className="flex items-center justify-center w-full cursor-pointer border border-gray-600 rounded-md py-2 px-3 bg-gray-50 hover:bg-blue-100">
                      <span className="text-sm font-medium">
                        {selectedFileName ? "Change File" : "Choose PDF File"}
                      </span>
                      <input
                        id="uploadedResult"
                        name="uploadedResult"
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={handleFileChange}
                        disabled={disabled || isUploading}
                      />
                    </label>

                    {/* Display selected filename or default message */}
                    <p className="text-sm text-gray-600 mt-1 text-center">
                      {selectedFileName ? (
                        <span className="text-blue-600 font-medium">
                          {selectedFileName}
                        </span>
                      ) : (
                        <span className="italic text-gray-400">
                          No file chosen
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <input
                    type={field.type}
                    name={field.name}
                    value={formData[field.name] || ""}
                    onChange={handleChange}
                    placeholder={field.placeholder}
                    disabled={
                      disabled || field.disabled || field.name === "createdDate"
                    }
                    required={field.required}
                    className={`${widthClass} p-1 border border-gray-600 rounded-md focus:outline-2 outline-blue-600 disabled:bg-gray-100`}
                  />
                )}
              </div>

              {errors[field.name] && (
                <p className="text-sm text-red-600 ml-40 mt-1">
                  {errors[field.name]}
                </p>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex gap-4 mt-2">
        <button
          type="submit"
          disabled={
            disabled || isUploading || Object.values(errors).some(Boolean)
          }
          className="w-44 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
        >
          {isUploading
            ? "Uploading..."
            : disabled
              ? "Saving..."
              : isEdit
                ? "Update"
                : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={disabled}
          className="w-44 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default LeadForm;
